/**
 * CONSUMOS DE INVENTARIO DE UNA OT DE LIMPIEZA (FASE 9.6-A)
 *
 * Sección «Consumos» de la ficha de la OT, agrupada por línea. Registrar un
 * consumo NO mueve stock: queda pendiente de validación. Al VALIDAR (ADMIN,
 * CONSULTOR o ENCARGADO de su centro) se genera una salida de stock (operación
 * CONSUMO_OT) que ya no se edita ni se rectifica. Sin borrador ni series.
 *
 * Una OT con consumos pendientes no se cierra sola (aviso arriba de la ficha
 * con «Validar pendientes»). OPERARIO no ve nada de esto: ni la sección ni el
 * aviso (la ficha sólo la pinta con `consumos_ot:leer`, y la RLS no le da filas).
 *
 * Móvil primero: tarjetas por consumo (mismas piezas que Materiales de un PT);
 * el formulario va almacén → artículo → lote → cantidad y unidad.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  api,
  type AlmacenesConsumoOt,
  type ConsumoOt,
  type ConsumosOt,
  type LoteConsumoOt,
  type OpcionArticuloStock,
  type OtDetalle,
  type EntregasMaterialOt,
  type PrevistoOt,
  type PrevistosOt,
} from '../api.ts';
import { Error as ErrorCaja, Estado as EstadoBadge, fecha, fechaDeInstante, fechaHora, Modal, useDatos } from '../componentes/base.tsx';
import { Buscador } from '../componentes/buscador.tsx';
import { estadoConsumoOt } from '../estados.ts';
import { useSesion } from '../contexto.tsx';
import { EntregasLinea } from './entregas-ot.tsx';
import { PrevistosLinea } from './materiales-previstos.tsx';
import { buscarArticulos, erroresDe, ErrorStock, nombreArticulo } from './stock.tsx';
import { codigoAlmacen, codigoVisible } from '../componentes/codigo-articulo.tsx';

export const TEXTO_REGISTRO_OT = 'Registrar material no descuenta stock: se descuenta cuando quien gestiona la OT lo valida.';
export const TEXTO_VALIDAR_OT = 'Al validar se generará una salida de stock y no podrá editarse.';
export const avisoPendientes = (n: number): string =>
  `Hay ${String(n)} ${n === 1 ? 'consumo pendiente' : 'consumos pendientes'} de validar. La OT no podrá cerrarse automáticamente hasta resolverlos.`;

const cant = (c: { cantidad: string; unidad: { codigo: string } }): string => `${c.cantidad} ${c.unidad.codigo}`;
type Datos = ReturnType<typeof useDatos<ConsumosOt | null>>;

/** Carga de la sección (null si quien mira no tiene `consumos_ot:leer`: ni se pide). */
export function useConsumosOt(idOt: string, lee: boolean): Datos {
  return useDatos(() => (lee ? api.get<ConsumosOt>(`/api/ots/${idOt}/consumos`) : Promise.resolve(null)), [idOt, lee]);
}

// =================================================================== aviso

/** Aviso de cabecera de la ficha: pendientes que retienen el cierre, con «Validar pendientes». */
export function AvisoConsumosPendientes({ datos, alValidar }: { datos: ConsumosOt | null; alValidar: () => void }): ReactNode {
  if (datos === null || datos.resumen.pendientes === 0) return null;
  return (
    <div className={`nota ${datos.cierreRetenido ? 'aviso' : 'info'} aviso-consumos-ot fila`} role="status" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
      <span>{avisoPendientes(datos.resumen.pendientes)}</span>
      <span className="fila" style={{ gap: 8, flexWrap: 'wrap' }}>
        <a href="#consumos">Ver consumos</a>
        {datos.puede.validar && <button type="button" className="principal" onClick={alValidar}>Validar pendientes</button>}
      </span>
    </div>
  );
}

// =================================================================== sección

type Dialogo = null | { t: 'nuevo'; idLinea: string } | { t: 'editar'; c: ConsumoOt } | { t: 'validar'; ids: string[] }
  | { t: 'rechazar'; c: ConsumoOt } | { t: 'anular'; c: ConsumoOt };

/** F10.2: material de una línea sin actividad — una frase y, a demanda, los bloques para preverlo o entregarlo. */
function MaterialVacioLinea({ ofrecer, children }: { ofrecer: boolean; children: ReactNode }): ReactNode {
  const [abierto, setAbierto] = useState(false);
  return (
    <div className="fila" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <p className="suave pequena" style={{ margin: 0 }}>Sin material previsto, entregado ni consumido.</p>
      {ofrecer && <button type="button" className="pequeno" style={{ minHeight: 44 }} aria-expanded={abierto} onClick={() => setAbierto((v) => !v)}>
        {abierto ? 'Ocultar' : 'Prever o entregar material'}
      </button>}
      {abierto && <div style={{ flexBasis: '100%' }}>{children}</div>}
    </div>
  );
}

export function ConsumosOtSeccion({ ot, datos, alCambiar, validarTodos, alCerrarValidarTodos }: {
  ot: OtDetalle; datos: Datos; alCambiar: () => void;
  /** «Validar pendientes» pulsado desde el aviso de cabecera. */
  validarTodos: boolean; alCerrarValidarTodos: () => void;
}): ReactNode {
  const base = `/api/ots/${ot.id}`;
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [error, setError] = useState<unknown>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const d = datos.datos;
  const pendientes = (d?.consumos ?? []).filter((c) => c.estado === 'PENDIENTE_VALIDACION');
  //  FASE 9.6-B1: previsto vs real por línea (mismo ámbito que los consumos).
  const { puede } = useSesion();
  const leePrevistos = puede('previstos_ot:leer');
  const prev = useDatos(() => (leePrevistos ? api.get<PrevistosOt>(`/api/ots/${ot.id}/previstos`) : Promise.resolve(null)), [ot.id, leePrevistos]);
  const previstosDe = (idLinea: string): PrevistoOt[] => (prev.datos?.previstos ?? []).filter((p) => p.idLinea === idLinea);
  //  FASE 9.6-B2: entregas y devoluciones logísticas (sin stock) por línea.
  const leeEntregas = puede('entregas_ot:leer');
  const ent = useDatos(() => (leeEntregas ? api.get<EntregasMaterialOt>(`/api/ots/${ot.id}/entregas`) : Promise.resolve(null)), [ot.id, leeEntregas]);
  const recargarMaterial = (): void => { prev.recargar(); ent.recargar(); };

  useEffect(() => {
    if (validarTodos && pendientes.length > 0) { setError(null); setDialogo({ t: 'validar', ids: pendientes.map((c) => c.id) }); }
    if (validarTodos) alCerrarValidarTodos();
  }, [validarTodos]); // eslint-disable-line react-hooks/exhaustive-deps

  //  Trazabilidad MOV → OT: «#consumo-<id>» lleva al consumo (y lo resalta).
  useEffect(() => {
    if (d === null || !window.location.hash.startsWith('#consumo')) return;
    document.getElementById(window.location.hash.slice(1))?.scrollIntoView?.({ block: 'center' });
  }, [d]);

  async function accion(f: () => Promise<unknown>, ok?: (r: unknown) => void): Promise<boolean> {
    setError(null);
    setAviso(null);
    setOcupado(true);
    try {
      const r = await f();
      setDialogo(null);
      ok?.(r);
      datos.recargar();
      prev.recargar();
      ent.recargar();
      alCambiar();
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      setOcupado(false);
    }
  }
  const avisoOps = (r: unknown): void => {
    const ops = (r as ConsumosOt).operaciones ?? [];
    if (ops.length > 0) setAviso(`Validado. Salida de stock: ${ops.map((o) => o.codigo).join(', ')}.`);
  };

  if (d === null) {
    if (datos.error !== null) return <section className="panel" aria-label="Consumos" id="consumos"><div className="panel-cuerpo"><ErrorCaja error={datos.error} reintentar={datos.recargar} /></div></section>;
    return null;
  }
  const porLinea = (idLinea: string): ConsumoOt[] => d.consumos.filter((c) => c.idLinea === idLinea);
  const aValidar = dialogo?.t === 'validar' ? d.consumos.filter((c) => dialogo.ids.includes(c.id)) : [];

  return (
    <section className="panel seccion-consumos-ot" aria-label="Consumos" id="consumos">
      <div className="panel-cabecera fila" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h2>Consumos</h2>
        {d.puede.validar && pendientes.length > 0 && (
          <button type="button" onClick={() => { setError(null); setDialogo({ t: 'validar', ids: pendientes.map((c) => c.id) }); }}>
            Validar pendientes ({pendientes.length})
          </button>
        )}
      </div>
      <div className="panel-cuerpo">
        {/*  F10.2: sin ningún consumo, cuatro ceros no dicen nada — el resumen sólo cuando hay algo. */}
        {d.consumos.length > 0 && (
          <div className="cifras resumen-materiales" aria-label="Resumen de consumos">
            <span className="cifra"><strong>{d.resumen.pendientes}</strong> pendientes</span>
            <span className="cifra"><strong>{d.resumen.validados}</strong> validados</span>
            <span className="cifra"><strong>{d.resumen.rechazados}</strong> rechazados</span>
            <span className="cifra"><strong>{d.resumen.anulados}</strong> anulados</span>
          </div>
        )}
        {d.puede.registrar && d.consumos.length === 0 && <p className="suave pequena">{TEXTO_REGISTRO_OT}</p>}
        {!d.puede.registrar && <p className="suave pequena">La OT está {d.ot.estado.toLowerCase().replace('_', ' ')}: no admite consumos nuevos.</p>}
        {aviso !== null && <p className="nota exito" role="status">{aviso}</p>}
        {error !== null && dialogo === null && <ErrorStock error={error} />}
        <div className="consumos-por-linea">
          {ot.lineas.map((l) => {
            const lista = porLinea(l.id);
            //  F10.2: una línea sin previsto, sin entregas y sin consumos no ocupa tres bloques «Sin…»: una sola
            //  frase, y «Prever o entregar material» despliega el mismo acceso de siempre. El contenedor y su
            //  cabecera (con «+ Añadir consumo») no cambian: nada salta de sitio al cargar.
            //  Sólo con previstos y entregas ya cargados: plegar antes haría saltar la línea de forma al llegar.
            const cargado = (!leePrevistos || prev.datos !== null) && (!leeEntregas || ent.datos !== null);
            const vacia = cargado && lista.length === 0
              && previstosDe(l.id).length === 0
              && (prev.datos?.noPrevistos ?? []).every((n) => n.idLinea !== l.id)
              && (ent.datos?.entregas ?? []).every((e) => e.idLinea !== l.id);
            return (
              <div key={l.id} className="consumos-linea" id={`consumos-linea-${l.id}`} role="group" aria-label={`Consumos de la línea ${String(l.orden)} · ${l.ubicacionCodigo}`}>
                <div className="fila consumos-linea-cabecera" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <h3 className="pequena"><span className="mono">#{l.orden}</span> {l.ubicacionCodigo} · {l.descripcionTarea ?? l.tipoDescripcion}</h3>
                  {d.puede.registrar && (
                    <button type="button" className="pequeno" onClick={() => { setError(null); setDialogo({ t: 'nuevo', idLinea: l.id }); }}>+ Añadir consumo</button>
                  )}
                </div>
                {vacia && <MaterialVacioLinea ofrecer={prev.datos?.editable === true || ent.datos?.editable === true}>
                  {prev.datos !== null && (
                    <PrevistosLinea idOt={ot.id} idLinea={l.id} previstos={[]} editable={prev.datos.editable} noPrevistos={[]} alCambiar={recargarMaterial} />
                  )}
                  {ent.datos !== null && (
                    <EntregasLinea idOt={ot.id} idLinea={l.id} datos={ent.datos} previstos={[]} alCambiar={recargarMaterial} />
                  )}
                </MaterialVacioLinea>}
                {!vacia && prev.datos !== null && (
                  <PrevistosLinea idOt={ot.id} idLinea={l.id} previstos={previstosDe(l.id)} editable={prev.datos.editable}
                    noPrevistos={prev.datos.noPrevistos.filter((n) => n.idLinea === l.id)} alCambiar={recargarMaterial} />
                )}
                {!vacia && ent.datos !== null && (
                  <EntregasLinea idOt={ot.id} idLinea={l.id} datos={ent.datos} previstos={previstosDe(l.id)} alCambiar={recargarMaterial} />
                )}
                {vacia ? null : lista.length === 0 ? <p className="suave pequena">Sin consumos.</p> : (
                  <ul className="lista-materiales" aria-label={`Consumos de la línea ${String(l.orden)}`}>
                    {lista.map((c) => (
                      <TarjetaConsumoOt key={c.id} c={c} d={d} ocupado={ocupado}
                        validar={() => { setError(null); setDialogo({ t: 'validar', ids: [c.id] }); }}
                        editar={() => { setError(null); setDialogo({ t: 'editar', c }); }}
                        rechazar={() => { setError(null); setDialogo({ t: 'rechazar', c }); }}
                        anular={() => { setError(null); setDialogo({ t: 'anular', c }); }} />
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {dialogo?.t === 'nuevo' && (
        <FormConsumoOt idOt={ot.id} linea={ot.lineas.find((l) => l.id === dialogo.idLinea)!} puedeValidar={d.puede.validar} error={error}
          previstos={previstosDe(dialogo.idLinea)}
          cerrar={() => setDialogo(null)}
          enviar={(cuerpo) => accion(() => api.post(`${base}/lineas/${dialogo.idLinea}/consumos`, cuerpo), avisoOps)} />
      )}
      {dialogo?.t === 'editar' && (
        <FormConsumoOt idOt={ot.id} linea={ot.lineas.find((l) => l.id === dialogo.c.idLinea)!} puedeValidar={false} consumo={dialogo.c} error={error}
          previstos={previstosDe(dialogo.c.idLinea)}
          cerrar={() => setDialogo(null)}
          enviar={(cuerpo) => accion(() => api.patch(`${base}/consumos/${dialogo.c.id}`, { ...cuerpo, versionEsperada: dialogo.c.version }))} />
      )}
      {dialogo?.t === 'validar' && (
        <Modal titulo="Validar consumos" cerrar={() => setDialogo(null)}
          pie={<><button onClick={() => setDialogo(null)}>Cancelar</button>
            <button className="principal" disabled={ocupado || aValidar.length === 0}
              onClick={() => void accion(() => api.post(`${base}/consumos/validar`, { ids: aValidar.map((c) => c.id) }), avisoOps)}>
              Validar {aValidar.length} {aValidar.length === 1 ? 'consumo' : 'consumos'}</button></>}>
          {error !== null && <ErrorStock error={error} />}
          <p><strong>{d.ot.codigo}</strong> · {aValidar.length} {aValidar.length === 1 ? 'consumo' : 'consumos'}</p>
          <ul className="lista-validar-consumos">
            {aValidar.map((c) => (
              <li key={c.id} className="pequena">
                <strong>{nombreArticulo(c.articulo)}</strong> · {cant(c)} · {codigoAlmacen(c.almacen)}{c.lote !== null && ` · lote ${c.lote.codigo}`}
                {c.lote?.caducado === true && <span className="etiqueta error">Lote caducado</span>}
                <span className={c.disponible !== null && Number(c.disponible) < Number(c.cantidadStock) ? ' saldo-negativo' : ' suave'}>
                  {' '}· disponible {c.disponible ?? '—'} {c.articulo.unidadStock.codigo}</span>
              </li>
            ))}
          </ul>
          <p className="nota aviso" role="note">{TEXTO_VALIDAR_OT}</p>
          <p className="suave pequena">El disponible es informativo: el stock se comprueba al validar. Si un consumo no puede salir, no se valida ninguno.</p>
        </Modal>
      )}
      {(dialogo?.t === 'rechazar' || dialogo?.t === 'anular') && (
        <DialogoMotivoOt titulo={dialogo.t === 'rechazar' ? 'Rechazar consumo' : 'Anular consumo'}
          explicacion={dialogo.t === 'rechazar' ? 'No mueve stock. Quien lo registró verá el motivo y podrá registrar uno corregido.'
            : 'El consumo queda anulado. No mueve stock.'}
          error={error} cerrar={() => setDialogo(null)}
          enviar={(motivo) => void accion(() => api.post(`${base}/consumos/${dialogo.c.id}/${dialogo.t === 'rechazar' ? 'rechazar' : 'anular'}`,
            { motivo, versionEsperada: dialogo.c.version }))} />
      )}
    </section>
  );
}

// ================================================================== tarjeta

function TarjetaConsumoOt({ c, d, ocupado, validar, editar, rechazar, anular }: {
  c: ConsumoOt; d: ConsumosOt; ocupado: boolean; validar: () => void; editar: () => void; rechazar: () => void; anular: () => void;
}): ReactNode {
  const pendiente = c.estado === 'PENDIENTE_VALIDACION';
  return (
    <li className={`tarjeta-material estado-${c.estado.toLowerCase()}`} id={`consumo-${c.id}`} aria-label={`${codigoVisible(c.articulo)} · ${c.articulo.descripcion}`}>
      <div className="fila tarjeta-material-cabecera" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div><strong>{c.articulo.descripcion}</strong> <Link className="mono pequena" to={`/inventario/articulos/${c.articulo.id}`}>{c.articulo.codigo}</Link>
          <div className="cantidad-material">{cant(c)}{c.unidad.id !== c.articulo.unidadStock.id && <span className="suave pequena"> (= {c.cantidadStock} {c.articulo.unidadStock.codigo})</span>}</div>
        </div>
        <EstadoBadge d={estadoConsumoOt(c.estado)} />
      </div>
      <div className="pequena datos-material">
        <span>Almacén {codigoAlmacen(c.almacen)}</span>
        {c.lote !== null && <span>Lote <Link className="mono" to={`/inventario/lotes/${c.lote.id}`}>{c.lote.codigo}</Link>{c.lote.fechaCaducidad !== null && ` · cad. ${fecha(c.lote.fechaCaducidad)}`}
          {c.lote.caducado && <span className="etiqueta error">Lote caducado</span>}</span>}
        {c.operacion !== null && <span>Salida <Link className="mono" to={`/inventario/operaciones/${c.operacion.id}`}>{c.operacion.codigo}</Link></span>}
        {c.ciclo !== null && <span className="etiqueta info">Reapertura del {fechaDeInstante(c.ciclo.desde)}</span>}
        {c.previsto != null && (c.previsto.codigo === c.articulo.codigo
          ? <span>Previsto: {c.previsto.descripcion}</span>
          : <span className="etiqueta info">Sustituye al previsto {c.previsto.descripcion}</span>)}
      </div>
      {c.observaciones !== null && <p className="pequena suave">{c.observaciones}</p>}
      {c.estado === 'RECHAZADO' && <p className="nota error pequena">Rechazado por {c.rechazadoPor?.nombre}: {c.motivoRechazo}</p>}
      {c.estado === 'ANULADO' && c.motivoAnulacion !== null && <p className="pequena suave">Anulado: {c.motivoAnulacion}</p>}
      <div className="suave pequena">Registrado por {c.registradoPor.nombre} · {fechaHora(c.registradoEn)}{c.validadoPor !== null && ` · validado por ${c.validadoPor.nombre}`}</div>
      {pendiente && (
        <div className="fila acciones-material" style={{ flexWrap: 'wrap' }}>
          {d.puede.validar && <button type="button" className="principal" disabled={ocupado} onClick={validar}>Validar</button>}
          {d.puede.registrar && <button type="button" onClick={editar}>Editar</button>}
          {d.puede.rechazar && <button type="button" onClick={rechazar}>Rechazar</button>}
          {d.puede.anular && <button type="button" onClick={anular}>Anular</button>}
        </div>
      )}
    </li>
  );
}

// ============================================================== formularios

function DialogoMotivoOt({ titulo, explicacion, error, cerrar, enviar }: {
  titulo: string; explicacion: string; error: unknown; cerrar: () => void; enviar: (motivo: string) => void;
}): ReactNode {
  const [motivo, setMotivo] = useState('');
  const campos = erroresDe(error);
  return (
    <Modal titulo={titulo} cerrar={cerrar}
      pie={<><button onClick={cerrar}>Volver</button><button className="principal" disabled={motivo.trim().length < 5} onClick={() => enviar(motivo.trim())}>{titulo}</button></>}>
      {error !== null && Object.keys(campos).length === 0 && <ErrorStock error={error} />}
      <p>{explicacion}</p>
      <div className="campo"><label htmlFor="mot-cot">Motivo (obligatorio)</label>
        <textarea id="mot-cot" rows={2} maxLength={2000} value={motivo} onChange={(ev) => setMotivo(ev.target.value)} />
        {campos['motivo'] !== undefined && <div className="error-campo">{campos['motivo']}</div>}</div>
    </Modal>
  );
}

/** Lotes con stock del artículo en el almacén, en orden FEFO; la recomendación nunca se elige sola. */
export function SelectorLoteOt({ idOt, idArticulo, idAlmacen, valor, alElegir, unidad, error }: {
  idOt: string; idArticulo: string; idAlmacen: string; valor: string | null; alElegir: (l: LoteConsumoOt) => void; unidad: string; error?: string | undefined;
}): ReactNode {
  const lotes = useDatos(() => api.get<{ filas: LoteConsumoOt[] }>(`/api/ots/${idOt}/consumos/lotes?${new URLSearchParams({ idArticulo, idAlmacen }).toString()}`),
    [idOt, idArticulo, idAlmacen]);
  const filas = lotes.datos?.filas ?? [];
  return (
    <fieldset className="campo selector-lote-ot">
      <legend>Lote *</legend>
      {lotes.cargando && lotes.datos === null && <p className="suave pequena">Cargando lotes…</p>}
      {lotes.error !== null && <ErrorCaja error={lotes.error} reintentar={lotes.recargar} />}
      {lotes.datos !== null && filas.length === 0 && <p className="nota aviso pequena">Ningún lote con stock en este almacén. Elige otro almacén o registra una entrada.</p>}
      {filas.map((l) => (
        <label key={l.id} className={`opcion-lote${l.recomendado ? ' recomendado' : ''}`}>
          <input type="radio" name="lote-consumo" value={l.id} checked={valor === l.id} onChange={() => alElegir(l)} />
          <span className="mono">{l.codigo}</span>
          <span className="pequena">{l.saldo} {unidad}</span>
          <span className="pequena suave">{l.fechaCaducidad === null ? 'sin caducidad' : `cad. ${fecha(l.fechaCaducidad)}`}</span>
          {l.caducidad.estado === 'CADUCADO' && <span className="etiqueta error">Caducado</span>}
          {l.recomendado && <span className="etiqueta exito">Recomendado (FEFO)</span>}
        </label>
      ))}
      {filas.some((l) => l.caducidad.estado === 'CADUCADO' && l.id === valor) && (
        <p className="nota aviso pequena" role="status">Lote caducado: se permite registrarlo, pero revisa antes de usarlo.</p>
      )}
      {error !== undefined && <div className="error-campo">{error}</div>}
    </fieldset>
  );
}

/** Alta (y edición de un pendiente) de un consumo de una línea: almacén → artículo → lote → cantidad y unidad. */
function FormConsumoOt({ idOt, linea, puedeValidar, consumo, error, cerrar, enviar, previstos = [] }: {
  idOt: string; linea: OtDetalle['lineas'][number]; puedeValidar: boolean; consumo?: ConsumoOt; error: unknown;
  /** FASE 9.6-B1: previstos de la línea (para «Corresponde a un previsto»). */
  previstos?: PrevistoOt[];
  cerrar: () => void; enviar: (cuerpo: Record<string, unknown>) => Promise<boolean>;
}): ReactNode {
  const editando = consumo !== undefined;
  const almacenes = useDatos(() => api.get<AlmacenesConsumoOt>(`/api/ots/${idOt}/consumos/almacenes`), [idOt]);
  const [idAlmacen, setIdAlmacen] = useState(consumo?.almacen.id ?? '');
  const [articulo, setArticulo] = useState<OpcionArticuloStock | null>(consumo === undefined ? null : {
    id: consumo.articulo.id, codigo: consumo.articulo.codigo, descripcion: consumo.articulo.descripcion, unidadStock: consumo.articulo.unidadStock,
    unidades: [{ ...consumo.articulo.unidadStock, factor: '1' }, ...(consumo.unidad.id !== consumo.articulo.unidadStock.id ? [{ ...consumo.unidad, factor: consumo.factorConversion }] : [])],
    saldo: consumo.disponible, trazabilidad: { controlaLotes: consumo.articulo.controlaLotes, controlaSeries: false, controlaCaducidad: consumo.articulo.controlaCaducidad },
  });
  const [idUnidad, setIdUnidad] = useState(consumo?.unidad.id ?? '');
  const [cantidad, setCantidad] = useState(consumo?.cantidad ?? '');
  const [idLote, setIdLote] = useState<string | null>(consumo?.lote?.id ?? null);
  const [observaciones, setObservaciones] = useState(consumo?.observaciones ?? '');
  const [idPrevisto, setIdPrevisto] = useState(consumo?.previsto?.id ?? '');
  const [enviando, setEnviando] = useState(false);
  const campos = erroresDe(error);
  const activos = previstos.filter((p) => p.estado === 'ACTIVO');
  //  Preselección SOLO si hay exactamente un previsto activo del mismo artículo en la línea. Se recalcula al cambiar de
  //  artículo (o cuando llegan los previstos), salvo que quien registra ya haya elegido a mano: entonces manda su elección.
  const [previstoTocado, setPrevistoTocado] = useState(editando);
  const clavePrevistos = activos.map((p) => `${p.id}:${p.articulo.id}`).join(',');
  useEffect(() => {
    if (previstoTocado) return;
    const mismos = articulo === null ? [] : activos.filter((p) => p.articulo.id === articulo.id);
    setIdPrevisto(mismos.length === 1 ? mismos[0]!.id : '');
  }, [articulo?.id, clavePrevistos, previstoTocado]); // eslint-disable-line react-hooks/exhaustive-deps

  //  0 → sin almacén; 1 (no móvil) → preseleccionado; más → elige quien registra.
  useEffect(() => {
    if (!editando && idAlmacen === '' && almacenes.datos?.preseleccion != null) setIdAlmacen(almacenes.datos.preseleccion);
  }, [almacenes.datos]); // eslint-disable-line react-hooks/exhaustive-deps

  const serializado = articulo?.trazabilidad?.controlaSeries === true;
  const conLotes = articulo?.trazabilidad?.controlaLotes === true;
  const listo = idAlmacen !== '' && articulo !== null && !serializado && (!conLotes || idLote !== null) && cantidad.trim() !== '';
  const sinAlmacen = almacenes.datos?.codigo === 'SIN_ALMACEN_CENTRO';

  async function guardar(accion: 'REGISTRAR' | 'VALIDAR'): Promise<void> {
    setEnviando(true);
    const cuerpo: Record<string, unknown> = {
      idAlmacen, idArticulo: articulo?.id ?? null, cantidad, idUnidad: idUnidad || articulo?.unidadStock.id,
      ...(conLotes ? { idLote } : { idLote: null }), observaciones,
      ...(activos.length > 0 || consumo?.previsto != null ? { idPrevisto: idPrevisto || null } : {}),
      ...(editando ? {} : { accion }),
    };
    try { await enviar(cuerpo); } finally { setEnviando(false); }
  }
  return (
    <Modal titulo={editando ? 'Editar consumo pendiente' : 'Añadir consumo'} cerrar={cerrar}
      pie={<>
        <button type="button" onClick={cerrar}>Cancelar</button>
        {editando ? <button type="button" className="principal" disabled={!listo || enviando} onClick={() => void guardar('REGISTRAR')}>Guardar cambios</button> : <>
          <button type="button" className={puedeValidar ? '' : 'principal'} disabled={!listo || enviando} onClick={() => void guardar('REGISTRAR')}>Registrar</button>
          {puedeValidar && <button type="button" className="principal" disabled={!listo || enviando} onClick={() => void guardar('VALIDAR')}>Registrar y validar</button>}
        </>}
      </>}>
      <div onKeyDown={(ev) => { if (ev.key === 'Enter' && (ev.target as HTMLElement).tagName === 'INPUT') ev.preventDefault(); }}>
        <p className="pequena">Línea <span className="mono">#{linea.orden}</span> · {linea.ubicacionCodigo} · {linea.descripcionTarea ?? linea.tipoDescripcion}</p>
        <p className="nota info pequena" role="note">{TEXTO_REGISTRO_OT}</p>
        {error !== null && Object.keys(campos).length === 0 && <ErrorStock error={error} />}
        <div className="form-material">
          <div className="campo"><label htmlFor="cot-alm">Almacén *</label>
            {sinAlmacen ? <p className="nota aviso pequena" role="status">El centro de esta OT no tiene ningún almacén operativo para ti. Pide que se dé de alta uno (Inventario → Almacenes).</p> : (
              <select id="cot-alm" value={idAlmacen} disabled={almacenes.datos === null}
                onChange={(ev) => { setIdAlmacen(ev.target.value); setArticulo(null); setIdLote(null); }}>
                <option value="">{almacenes.datos === null ? 'Cargando…' : 'Elige el almacén'}</option>
                {(almacenes.datos?.filas ?? []).map((a) => <option key={a.id} value={a.id}>{codigoAlmacen(a)} · {a.descripcion}{a.tipo === 'MOVIL' ? ' (móvil)' : ''}</option>)}
              </select>
            )}
            {campos['idAlmacen'] !== undefined && <div className="error-campo">{campos['idAlmacen']}</div>}</div>
          <Buscador<OpcionArticuloStock> etiqueta="Artículo *" valor={articulo} contexto={idAlmacen} deshabilitado={idAlmacen === ''}
            placeholder={idAlmacen === '' ? 'Elige antes el almacén' : 'Código o descripción…'}
            buscar={buscarArticulos(idAlmacen === '' ? null : idAlmacen)} clave={(a) => a.id} texto={nombreArticulo}
            detalle={(a) => `Disponible ${a.saldo ?? '0'} ${a.unidadStock.codigo}${a.trazabilidad?.controlaSeries === true ? ' · con nº de serie (no admitido)' : ''}`}
            alElegir={(a) => { setArticulo(a); setIdUnidad(a?.unidadStock.id ?? ''); setIdLote(null); setPrevistoTocado(false); }}
            error={campos['idArticulo']} sinResultados="Ningún artículo activo configurado en este almacén." />
          {serializado && <p className="nota error pequena" role="alert">Este artículo se controla por número de serie: los consumos de Limpieza no admiten artículos serializados.</p>}
          {articulo !== null && !serializado && conLotes && idAlmacen !== '' && (
            <SelectorLoteOt idOt={idOt} idArticulo={articulo.id} idAlmacen={idAlmacen} valor={idLote} alElegir={(l) => setIdLote(l.id)}
              unidad={articulo.unidadStock.codigo} error={campos['idLote']} />
          )}
          {articulo !== null && !serializado && (
            <div className="form-rejilla">
              <div className="campo"><label htmlFor="cot-cant">Cantidad *</label>
                <input id="cot-cant" inputMode="decimal" value={cantidad} onChange={(ev) => setCantidad(ev.target.value)} />
                {campos['cantidad'] !== undefined && <div className="error-campo">{campos['cantidad']}</div>}</div>
              <div className="campo"><label htmlFor="cot-ud">Unidad *</label>
                <select id="cot-ud" value={idUnidad} onChange={(ev) => setIdUnidad(ev.target.value)}>
                  {articulo.unidades.map((u) => <option key={u.id} value={u.id}>{u.codigo}{u.factor === '1' ? '' : ` (= ${u.factor} ${articulo.unidadStock.codigo})`}</option>)}
                </select>
                {campos['idUnidad'] !== undefined && <div className="error-campo">{campos['idUnidad']}</div>}</div>
            </div>
          )}
          {articulo !== null && !serializado && !conLotes && (
            <p className="suave pequena" aria-live="polite">Disponible aquí: <strong>{articulo.saldo ?? '0'} {articulo.unidadStock.codigo}</strong> (informativo: se comprueba al validar)</p>
          )}
          {(activos.length > 0 || consumo?.previsto != null) && (
            <div className="campo"><label htmlFor="cot-prev">Corresponde a un previsto</label>
              <select id="cot-prev" value={idPrevisto} onChange={(ev) => { setIdPrevisto(ev.target.value); setPrevistoTocado(true); }}>
                <option value="">No previsto</option>
                {activos.map((p) => <option key={p.id} value={p.id}>{p.articulo.descripcion} · {p.cantidad} {p.unidad.codigo}</option>)}
                {consumo?.previsto != null && !activos.some((p) => p.id === consumo.previsto!.id) && (
                  <option value={consumo.previsto.id}>{consumo.previsto.descripcion} (retirado)</option>
                )}
              </select>
              {articulo !== null && idPrevisto !== '' && activos.find((p) => p.id === idPrevisto)?.articulo.id !== articulo.id && (
                <p className="suave pequena">Otro artículo que el previsto: quedará como sustitución en ejecución.</p>
              )}
              {campos['idPrevisto'] !== undefined && <div className="error-campo">{campos['idPrevisto']}</div>}</div>
          )}
          <div className="campo"><label htmlFor="cot-obs">Observaciones</label>
            <textarea id="cot-obs" rows={2} maxLength={1000} value={observaciones} onChange={(ev) => setObservaciones(ev.target.value)} />
            {campos['observaciones'] !== undefined && <div className="error-campo">{campos['observaciones']}</div>}</div>
        </div>
      </div>
    </Modal>
  );
}
